import React, { memo, useRef } from 'react';
import { IconButton } from '@mui/material';

import ControlledError from '../ControlledError';
import { ReactComponent as DeleteIcon } from 'assets/icons/ClearRed.svg';
import { ReactComponent as FileUploadIcon } from 'assets/icons/FileUploadIcon.svg';
import { createGuid } from 'utils/generalUtils';
import { useStyles } from './styles';

export interface IUploadedFile {
  id: string;
  name: string;
  file?: File; // present for new files
  url?: string; // blob URL for new files, http URL for existing files
  isExisting?: boolean; // optional flag to distinguish new from existing files
}

export interface IFileUploadProps {
  files: IUploadedFile[];
  onChange: (files: IUploadedFile[]) => void;
  maxFiles?: number;
  maxFileSize?: number; // bytes — files larger than this are rejected
  accept?: string;
  uploadHintPrefix?: string;
  uploadHint?: string;
  maxFilesHint?: string;
  error?: string;
  maxFileSizeError?: string; // shown via onError when a file exceeds maxFileSize
  invalidFileTypeError?: string; // shown via onError when a file doesn't match `accept`
  onError?: (message: string) => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  styleClass?: string;
  /** Base id for automation. The dropzone uses `${testId}-dropzone`, the hidden input `${testId}-input`,
   * and each selected file's remove button `${testId}-remove-button-{index}`. */
  testId?: string;
}

// The input's `accept` attribute only filters the OS picker — it can be bypassed (drag/drop into
// other contexts, choosing "All Files", or a tampered DOM). This re-validates each selected file
// against `accept` by both extension and MIME type so disallowed files never enter the form.
const isFileAccepted = (file: File, accept?: string): boolean => {
  if (!accept) return true;
  const tokens = accept
    .split(',')
    .map(token => token.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return true;

  const fileName = file.name.toLowerCase();
  const mimeType = file.type.toLowerCase();

  return tokens.some(token => {
    if (token.startsWith('.')) return fileName.endsWith(token); // extension, e.g. ".pdf"
    if (token.endsWith('/*')) return mimeType.startsWith(token.slice(0, token.indexOf('/') + 1)); // wildcard, e.g. "image/*"
    return mimeType === token; // exact MIME type, e.g. "application/pdf"
  });
};

const FileUpload = ({
  files,
  onChange,
  maxFiles = 3,
  maxFileSize,
  accept = '.pdf',
  uploadHintPrefix,
  uploadHint,
  maxFilesHint,
  error,
  maxFileSizeError,
  invalidFileTypeError,
  onError,
  disabled,
  icon,
  styleClass,
  testId,
}: IFileUploadProps) => {
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);

  const isMaxReached = files.length >= maxFiles;
  const canInteract = !disabled && !isMaxReached;

  const handleZoneClick = () => {
    if (!canInteract) return;
    inputRef.current?.click();
  };

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (selectedFiles.length === 0) return;

    // Re-validate file type — the `accept` attribute is only a picker hint and can be bypassed.
    const typedFiles = selectedFiles.filter(file => isFileAccepted(file, accept));
    if (typedFiles.length < selectedFiles.length && invalidFileTypeError) {
      onError?.(invalidFileTypeError);
    }
    if (typedFiles.length === 0) return;

    // Reject oversized files before consuming slots — surface the error via onError.
    const validFiles =
      maxFileSize != null ? typedFiles.filter(file => file.size <= maxFileSize) : typedFiles;
    if (validFiles.length < typedFiles.length && maxFileSizeError) {
      onError?.(maxFileSizeError);
    }
    if (validFiles.length === 0) return;

    const remainingSlots = maxFiles - files.length;
    const newFiles: IUploadedFile[] = validFiles.slice(0, remainingSlots).map(file => ({
      id: createGuid(),
      name: file.name,
      file,
    }));
    onChange([...files, ...newFiles]);
  };

  const handleRemove = (id: string) => {
    if (disabled) return;
    onChange(files.filter(f => f.id !== id));
  };

  return (
    <div className={`${classes.container} ${styleClass || ''}`} data-testid={testId}>
      {!isMaxReached && (
        <ControlledError show={!!error} error={error}>
          <div
            className={`${classes.dropzone} ${disabled ? classes.disabled : ''}`}
            data-testid={testId ? `${testId}-dropzone` : undefined}
            onClick={handleZoneClick}
            role="button"
            tabIndex={0}
          >
            {icon || <FileUploadIcon className={classes.fileIcon} />}
            <p className={classes.uploadText}>
              {uploadHintPrefix && <span className={classes.uploadHintPrefix}>{uploadHintPrefix} </span>}
              {uploadHint && <span className={classes.uploadHint}>{uploadHint}</span>}
            </p>
            {maxFilesHint && <p className={classes.maxFilesHint}>{maxFilesHint}</p>}
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              multiple={maxFiles > 1}
              data-testid={testId ? `${testId}-input` : undefined}
              onChange={handleFiles}
              className={classes.hiddenInput}
            />
          </div>
        </ControlledError>
      )}

      {files.length > 0 && (
        <div className={classes.filesList}>
          {files.map((file, index) => (
            <div key={file.id} className={classes.fileRow} data-testid={testId ? `${testId}-file-${index}` : undefined}>
              <span className={classes.fileName}>{file.name}</span>
              <IconButton
                size="small"
                aria-label="remove file"
                data-testid={testId ? `${testId}-remove-button-${index}` : undefined}
                className={classes.removeButton}
                onClick={() => handleRemove(file.id)}
                disabled={disabled}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(FileUpload);
